import { useState, useEffect } from 'react';
import { useAddress } from '@/context/AddressContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { X, MapPin, Plus, Check, Trash2, Edit2, Home, Briefcase, MapPinned, Map } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import MapPicker from './MapPicker';

const labelIcons = {
  Home: Home,
  Work: Briefcase,
  Other: MapPinned
};

const AddressModal = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useAuth();
  const { addresses, addAddress, updateAddress, deleteAddress, selectAddress, selectedAddress } = useAddress();
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const [formData, setFormData] = useState({
    type: 'Home',
    full_name: '',
    phone: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    pincode: '',
    is_default: false,
    lat: null,
    lng: null
  });

  useEffect(() => {
    if (isOpen && addresses.length === 0 && isAuthenticated) {
      setShowForm(true);
    }
  }, [isOpen, addresses.length, isAuthenticated]);

  const resetForm = () => {
    setFormData({
      type: 'Home',
      full_name: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      pincode: '',
      is_default: false,
      lat: null,
      lng: null
    });
    setEditingAddress(null);
    setShowMap(false);
    setMapPosition(null);
  };

  const handleEdit = (address) => {
    setFormData({
      type: address.type || 'Home',
      full_name: address.full_name,
      phone: address.phone,
      address_line_1: address.address_line_1,
      address_line_2: address.address_line_2 || '',
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      is_default: address.is_default,
      lat: address.lat || null,
      lng: address.lng || null
    });
    setEditingAddress(address);
    if (address.lat && address.lng) {
      setMapPosition([address.lat, address.lng]);
    }
    setShowForm(true);
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      await deleteAddress(addressId);
      toast.success('Address deleted');
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      lat: location.lat,
      lng: location.lng
    }));
  };

  const handleAddressFromMap = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address_line_1: addressData.address_line1 || prev.address_line_1,
      address_line_2: addressData.address_line2 || prev.address_line_2,
      city: addressData.city || prev.city,
      state: addressData.state || prev.state,
      pincode: addressData.pincode || prev.pincode,
      lat: addressData.lat,
      lng: addressData.lng
    }));
    toast.success('Location selected! Review and edit the address below.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.phone || !formData.address_line_1 || !formData.city || !formData.state || !formData.pincode) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      if (editingAddress) {
        await updateAddress(editingAddress.id, formData);
        toast.success('Address updated');
      } else {
        await addAddress(formData);
        toast.success('Address added');
      }
      resetForm();
      setShowForm(false);
    } catch (error) {
      toast.error(editingAddress ? 'Failed to update address' : 'Failed to add address');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAddress = (address) => {
    selectAddress(address);
    onClose();
    toast.success(`Delivering to ${address.city}`);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0047AB]" />
              <h2 className="text-lg font-outfit font-bold text-slate-900">
                {showForm ? (editingAddress ? 'Edit Address' : 'Add New Address') : 'Select Delivery Address'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-130px)]">
            {!isAuthenticated ? (
              <div className="p-6 text-center">
                <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600 mb-4">Please login to manage your addresses</p>
                <Button onClick={onClose} className="bg-[#0047AB] hover:bg-[#003380]">
                  Login to Continue
                </Button>
              </div>
            ) : showForm ? (
              /* Address Form */
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Map Toggle */}
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-slate-700">Pick Location on Map</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMap(!showMap)}
                    className={showMap ? 'bg-[#0047AB]/10 border-[#0047AB] text-[#0047AB]' : ''}
                  >
                    <Map className="w-4 h-4 mr-2" />
                    {showMap ? 'Hide Map' : 'Show Map'}
                  </Button>
                </div>

                {/* Map Picker */}
                {showMap && (
                  <MapPicker
                    initialPosition={mapPosition}
                    onLocationSelect={handleLocationSelect}
                    onAddressSelect={handleAddressFromMap}
                    height="250px"
                  />
                )}

                {/* Label Selection */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Address Type</Label>
                  <div className="flex gap-2 mt-2">
                    {['Home', 'Work', 'Other'].map((type) => {
                      const Icon = labelIcons[type];
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData({ ...formData, type })}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                            formData.type === type
                              ? 'border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]'
                              : 'border-slate-200 text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {type}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 9876543210"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address_line_1">Address Line 1 *</Label>
                  <Input
                    id="address_line_1"
                    value={formData.address_line_1}
                    onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                    placeholder="House/Flat No., Building Name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="address_line_2">Address Line 2</Label>
                  <Input
                    id="address_line_2"
                    value={formData.address_line_2}
                    onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                    placeholder="Street, Landmark (Optional)"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Mumbai"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="Maharashtra"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      value={formData.pincode}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      placeholder="400001"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="w-4 h-4 text-[#0047AB] rounded"
                  />
                  <Label htmlFor="is_default" className="text-sm cursor-pointer">Set as default address</Label>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      resetForm();
                      setShowForm(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#0047AB] hover:bg-[#003380]"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (editingAddress ? 'Update Address' : 'Save Address')}
                  </Button>
                </div>
              </form>
            ) : (
              /* Address List */
              <div className="p-4">
                {addresses.length === 0 ? (
                  <div className="text-center py-8">
                    <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500 mb-4">No saved addresses</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {addresses.map((address) => {
                      const Icon = labelIcons[address.type] || MapPinned;
                      const isSelected = selectedAddress?.id === address.id;
                      
                      return (
                        <div
                          key={address.id}
                          className={`relative p-4 rounded-xl border-2 transition-all cursor-pointer ${
                            isSelected 
                              ? 'border-[#0047AB] bg-[#0047AB]/5' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => handleSelectAddress(address)}
                          data-testid={`address-${address.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-[#0047AB]/10' : 'bg-slate-100'}`}>
                              <Icon className={`w-5 h-5 ${isSelected ? 'text-[#0047AB]' : 'text-slate-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-slate-900">{address.type || 'Home'}</span>
                                {address.is_default && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Default</span>
                                )}
                                {address.lat && address.lng && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Map className="w-3 h-3" /> Map
                                  </span>
                                )}
                              </div>
                              <p className="text-sm font-medium text-slate-700">{address.full_name}</p>
                              <p className="text-sm text-slate-500 line-clamp-2">
                                {address.address_line_1}
                                {address.address_line_2 && `, ${address.address_line_2}`}
                              </p>
                              <p className="text-sm text-slate-500">
                                {address.city}, {address.state} - {address.pincode}
                              </p>
                              <p className="text-sm text-slate-500">{address.phone}</p>
                            </div>
                            {isSelected && (
                              <Check className="w-5 h-5 text-[#0047AB] flex-shrink-0" />
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="absolute top-2 right-2 flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(address);
                              }}
                              className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4 text-slate-500" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(address.id);
                              }}
                              className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add New Address Button */}
                <button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  className="w-full mt-4 flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 hover:border-[#0047AB] hover:text-[#0047AB] transition-colors"
                  data-testid="add-address-btn"
                >
                  <Plus className="w-5 h-5" />
                  Add New Address
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AddressModal;
